/**
 * TrainingSession — Interactive Shadow → Correct → Debrief Wizard
 * 
 * The core training flow that guides users step-by-step:
 * 
 * Step 1: SHADOW — See what the replica did → Approve or Correct
 * Step 2: CORRECT — Edit the replica's response
 * Step 3: DEBRIEF — Explain why you made the changes
 * 
 * Each step posts to the real cognitive API endpoints.
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Eye, PenLine, MessageCircle, CheckCircle, ArrowRight,
    ArrowLeft, X, Send, Sparkles, AlertTriangle, ThumbsUp
} from 'lucide-react';
import { api } from '../../lib/api';

// ============================================
// TYPES
// ============================================

type SessionStep = 'SHADOW' | 'CORRECT' | 'DEBRIEF' | 'COMPLETE';

interface TrainingScenario {
    id: string;
    category: string;
    context: string;
    replicaAction: string;
    humanReference?: string;
    source?: 'live-chat' | 'demo';
}

interface CorrectionTag {
    id: string;
    label: string;
    selected: boolean;
}

interface TrainingSessionProps {
    replicaId: string;
    onComplete?: (result: SessionResult) => void;
    onClose?: () => void;
}

interface SessionResult {
    action: 'approve' | 'correct';
    corrections?: string;
    reasoning?: string;
    tags?: string[];
}

// ============================================
// DEMO SCENARIOS (until real shadow observations flow in)
// ============================================

const DEMO_SCENARIOS: TrainingScenario[] = [
    {
        id: 'email-client-1',
        category: 'email',
        context: 'A client named Sarah asks for the project timeline update. She has been waiting 3 days.',
        replicaAction: 'Dear Sarah,\n\nThank you for your inquiry regarding the project timeline. I wanted to provide you with an update on our current progress.\n\nThe development phase is proceeding according to schedule. We anticipate completing the main deliverables by the end of next week.\n\nPlease do not hesitate to reach out if you require any additional information.\n\nBest regards',
        humanReference: 'Hi Sarah,\n\nSorry for the delay! Here\'s where we are:\n\n- Backend: Done ✅\n- Frontend: 80% — wrapping up this week\n- Testing: Starts Monday\n\nWe\'re on track to deliver by Friday next week. I\'ll send you the staging link Wednesday so you can preview.\n\nLet me know if anything looks off!',
    },
    {
        id: 'decision-vendor-1',
        category: 'decision',
        context: 'Your team needs to choose between Vendor A (cheaper, slower support) and Vendor B (premium, 24/7 support) for a critical infrastructure service.',
        replicaAction: 'I recommend Vendor A because it offers a 40% cost saving. The slower support response time can be mitigated by building internal documentation and having backup procedures in place.',
        humanReference: 'Go with Vendor B. For critical infrastructure, 24/7 support is non-negotiable. The cost difference is worth it — a single outage without immediate support would cost us more than the annual price gap.',
    },
    {
        id: 'email-team-1',
        category: 'email',
        context: 'A team member just missed a deadline for the second time. You need to address it professionally.',
        replicaAction: 'Hi,\n\nI noticed that the deadline for the Q4 report was missed again. This is the second occurrence. Meeting deadlines is important for team productivity.\n\nPlease ensure this does not happen again. Let me know if there are any blockers.\n\nRegards',
    },
    {
        id: 'escalation-1',
        category: 'escalation',
        context: 'A customer is threatening to leave because of a billing error. They\'ve been overcharged €500 for 3 months.',
        replicaAction: 'Dear Customer,\n\nWe apologize for the billing discrepancy. I have escalated this issue to our billing department for review. You should receive a response within 5-7 business days.\n\nThank you for your patience.',
    },
    {
        id: 'meeting-prep-1',
        category: 'meeting',
        context: 'You have a board presentation tomorrow. The replica prepared talking points.',
        replicaAction: 'Board Meeting Talking Points:\n\n1. Revenue is up 12% QoQ\n2. Customer acquisition cost reduced by 8%\n3. New product launch scheduled for Q2\n4. Team expansion: 3 new hires planned\n5. Risk factors: Market volatility, competitor pricing',
    },
];

const CORRECTION_TAGS: CorrectionTag[] = [
    { id: 'tone', label: '🎨 Wrong Tone', selected: false },
    { id: 'context', label: '📋 Missed Context', selected: false },
    { id: 'judgment', label: '🧠 Bad Judgment', selected: false },
    { id: 'style', label: '✍️ Not My Style', selected: false },
    { id: 'accuracy', label: '❌ Inaccurate', selected: false },
    { id: 'too-formal', label: '👔 Too Formal', selected: false },
    { id: 'too-casual', label: '🤙 Too Casual', selected: false },
    { id: 'incomplete', label: '📝 Incomplete', selected: false },
];

// ============================================
// COMPONENT
// ============================================

export default function TrainingSession({ replicaId, onComplete, onClose }: TrainingSessionProps) {
    const [step, setStep] = useState<SessionStep>('SHADOW');
    const [scenario, setScenario] = useState<TrainingScenario | null>(null);
    const [loading, setLoading] = useState(true);
    const [pendingCount, setPendingCount] = useState(0);
    const [correction, setCorrection] = useState('');
    const [reasoning, setReasoning] = useState('');
    const [tags, setTags] = useState<CorrectionTag[]>([...CORRECTION_TAGS]);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<SessionResult | null>(null);

    // ============================================
    // LOAD REAL SCENARIOS (fall back to demo)
    // ============================================

    useEffect(() => {
        loadScenario();
    }, [replicaId]);

    async function loadScenario() {
        setLoading(true);
        try {
            const res = await api.get<any>(`/cognitive/${replicaId}/pending-reviews?limit=5`);
            const scenarios = res.data?.scenarios || [];
            setPendingCount(scenarios.length);

            if (scenarios.length > 0) {
                // Use a real chat interaction!
                const real = scenarios[0];
                setScenario({
                    id: real.id,
                    category: real.category || 'chat',
                    context: real.context,
                    replicaAction: real.replicaAction,
                    source: 'live-chat',
                });
            } else {
                // Fall back to demo scenarios
                const idx = Math.floor(Math.random() * DEMO_SCENARIOS.length);
                setScenario({ ...DEMO_SCENARIOS[idx], source: 'demo' });
            }
        } catch (err) {
            console.error('Failed to load pending reviews, using demo:', err);
            const idx = Math.floor(Math.random() * DEMO_SCENARIOS.length);
            setScenario({ ...DEMO_SCENARIOS[idx], source: 'demo' });
        } finally {
            setLoading(false);
        }
    }

    // ============================================
    // HANDLERS
    // ============================================

    const handleApprove = useCallback(async () => {
        setSubmitting(true);
        try {
            // Post shadow observation
            await api.post(`/cognitive/${replicaId}/shadow`, {
                category: scenario.category,
                context: scenario.context,
                humanAction: scenario.replicaAction,
            });

            // Post approval
            await api.post(`/cognitive/${replicaId}/approve`, {
                category: scenario.category,
                context: scenario.context,
                replicaAction: scenario.replicaAction,
                estimatedTimeSaved: 120, // 2 min saved
            });

            const sessionResult: SessionResult = { action: 'approve' };
            setResult(sessionResult);
            setStep('COMPLETE');
            onComplete?.(sessionResult);
        } catch (err) {
            console.error('Failed to record approval:', err);
        } finally {
            setSubmitting(false);
        }
    }, [replicaId, scenario, onComplete]);

    const handleSubmitCorrection = useCallback(async () => {
        if (!correction.trim()) return;
        setStep('DEBRIEF');
    }, [correction]);

    const handleSubmitDebrief = useCallback(async () => {
        setSubmitting(true);
        const selectedTags = tags.filter(t => t.selected).map(t => t.label);

        try {
            // Post shadow observation
            await api.post(`/cognitive/${replicaId}/shadow`, {
                category: scenario.category,
                context: scenario.context,
                humanAction: scenario.replicaAction,
            });

            // Post correction
            await api.post(`/cognitive/${replicaId}/correct`, {
                category: scenario.category,
                context: scenario.context,
                replicaAction: scenario.replicaAction,
                correction: correction,
                styleMatchDelta: selectedTags.includes('✍️ Not My Style') ? 30 : 60,
                judgmentDelta: selectedTags.includes('🧠 Bad Judgment') ? 20 : 50,
            });

            // Post debrief
            if (reasoning.trim()) {
                await api.post(`/cognitive/${replicaId}/debrief`, {
                    category: scenario.category,
                    context: scenario.context,
                    humanAction: correction,
                    reasoning: reasoning,
                });
            }

            // Record coaching session
            try {
                await api.post('/cognitive/coaching/record', {
                    timeSavedSec: 120,
                    corrections: 1,
                });
            } catch { /* non-critical */ }

            const sessionResult: SessionResult = {
                action: 'correct',
                corrections: correction,
                reasoning,
                tags: selectedTags,
            };
            setResult(sessionResult);
            setStep('COMPLETE');
            onComplete?.(sessionResult);
        } catch (err) {
            console.error('Failed to record training:', err);
        } finally {
            setSubmitting(false);
        }
    }, [replicaId, scenario, correction, reasoning, tags, onComplete]);

    const toggleTag = (tagId: string) => {
        setTags(prev => prev.map(t =>
            t.id === tagId ? { ...t, selected: !t.selected } : t
        ));
    };

    // ============================================
    // STEP INDICATOR
    // ============================================

    const stepIndicator = (
        <div style={styles.stepIndicator}>
            {(['SHADOW', 'CORRECT', 'DEBRIEF'] as SessionStep[]).map((s, idx) => {
                const icons = [
                    <Eye size={14} key="eye" />,
                    <PenLine size={14} key="pen" />,
                    <MessageCircle size={14} key="msg" />,
                ];
                const labels = ['Shadow', 'Correct', 'Debrief'];
                const isActive = step === s;
                const isDone = (step === 'CORRECT' && idx === 0) ||
                    (step === 'DEBRIEF' && idx <= 1) ||
                    step === 'COMPLETE';

                return (
                    <div key={s} style={styles.stepRow}>
                        <div style={{
                            ...styles.stepDot,
                            backgroundColor: isActive ? '#a78bfa' : isDone ? '#34d399' : 'rgba(255,255,255,0.1)',
                            boxShadow: isActive ? '0 0 12px rgba(167,139,250,0.4)' : 'none',
                        }}>
                            {isDone && !isActive ? <CheckCircle size={12} color="#fff" /> : icons[idx]}
                        </div>
                        <span style={{
                            ...styles.stepLabel,
                            color: isActive ? '#fff' : isDone ? '#34d399' : 'rgba(255,255,255,0.3)',
                            fontWeight: isActive ? 700 : 400,
                        }}>
                            {labels[idx]}
                        </span>
                        {idx < 2 && (
                            <div style={{
                                ...styles.stepConnector,
                                backgroundColor: isDone ? '#34d399' : 'rgba(255,255,255,0.08)',
                            }} />
                        )}
                    </div>
                );
            })}
        </div>
    );

    // ============================================
    // RENDER STEPS
    // ============================================

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <h3 style={styles.title}>
                    {step === 'COMPLETE' ? '✅ Training Complete' : '🧠 Training Session'}
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {scenario?.source === 'live-chat' && (
                        <span style={{
                            padding: '3px 10px',
                            borderRadius: 12,
                            background: 'rgba(52,211,153,0.15)',
                            border: '1px solid rgba(52,211,153,0.3)',
                            color: '#34d399',
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase' as const,
                            letterSpacing: 1,
                        }}>
                            🟢 LIVE
                        </span>
                    )}
                    {scenario?.source === 'demo' && (
                        <span style={{
                            padding: '3px 10px',
                            borderRadius: 12,
                            background: 'rgba(251,191,36,0.1)',
                            border: '1px solid rgba(251,191,36,0.2)',
                            color: '#fbbf24',
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase' as const,
                            letterSpacing: 1,
                        }}>
                            DEMO
                        </span>
                    )}
                    {onClose && (
                        <button onClick={onClose} style={styles.closeBtn}>
                            <X size={18} />
                        </button>
                    )}
                </div>
            </div>

            {/* Loading state */}
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>Loading training scenario...</span>
                </div>
            )}

            {/* Pending count */}
            {!loading && pendingCount > 0 && step === 'SHADOW' && (
                <div style={{
                    padding: '8px 14px',
                    borderRadius: 10,
                    background: 'rgba(52,211,153,0.08)',
                    border: '1px solid rgba(52,211,153,0.15)',
                    fontSize: 12,
                    color: '#34d399',
                    textAlign: 'center' as const,
                }}>
                    📬 {pendingCount} real chat interaction{pendingCount > 1 ? 's' : ''} waiting for your review
                </div>
            )}

            {step !== 'COMPLETE' && !loading && stepIndicator}

            {/* STEP 1: SHADOW */}
            {step === 'SHADOW' && !loading && scenario && (
                <div style={styles.stepContent}>
                    <div style={styles.contextCard}>
                        <div style={styles.contextBadge}>
                            <Eye size={14} color="var(--accent)" />
                            <span>Scenario: {scenario.category}</span>
                        </div>
                        <p style={styles.contextText}>{scenario.context}</p>
                    </div>

                    <div style={styles.replicaResponse}>
                        <div style={styles.responseHeader}>
                            <Sparkles size={14} color="#a78bfa" />
                            <span style={styles.responseTitle}>Your Replica wrote:</span>
                        </div>
                        <pre style={styles.responseText}>{scenario.replicaAction}</pre>
                    </div>

                    <div style={styles.actionRow}>
                        <button
                            onClick={handleApprove}
                            disabled={submitting}
                            style={styles.approveBtn}
                        >
                            <ThumbsUp size={16} />
                            <span>{submitting ? 'Approving...' : 'Approve — Looks Good'}</span>
                        </button>
                        <button
                            onClick={() => {
                                setCorrection(scenario.replicaAction);
                                setStep('CORRECT');
                            }}
                            style={styles.correctBtn}
                        >
                            <PenLine size={16} />
                            <span>Correct — I'd Change This</span>
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 2: CORRECT */}
            {step === 'CORRECT' && (
                <div style={styles.stepContent}>
                    <div style={styles.sectionHeader}>
                        <PenLine size={16} color="#f472b6" />
                        <span style={styles.sectionTitle}>Rewrite it your way</span>
                    </div>

                    <textarea
                        value={correction}
                        onChange={e => setCorrection(e.target.value)}
                        style={styles.textarea}
                        rows={8}
                        placeholder="Rewrite the response how YOU would write it..."
                    />

                    <div style={styles.sectionHeader}>
                        <AlertTriangle size={14} color="#fbbf24" />
                        <span style={{ ...styles.sectionTitle, fontSize: 13 }}>What was wrong?</span>
                    </div>

                    <div style={styles.tagsGrid}>
                        {tags.map(tag => (
                            <button
                                key={tag.id}
                                onClick={() => toggleTag(tag.id)}
                                style={{
                                    ...styles.tagBtn,
                                    backgroundColor: tag.selected ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.03)',
                                    borderColor: tag.selected ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.08)',
                                    color: tag.selected ? '#a78bfa' : 'rgba(255,255,255,0.6)',
                                }}
                            >
                                {tag.label}
                            </button>
                        ))}
                    </div>

                    <div style={styles.actionRow}>
                        <button
                            onClick={() => setStep('SHADOW')}
                            style={styles.backBtn}
                        >
                            <ArrowLeft size={14} />
                            <span>Back</span>
                        </button>
                        <button
                            onClick={handleSubmitCorrection}
                            disabled={!correction.trim()}
                            style={{
                                ...styles.nextBtn,
                                opacity: correction.trim() ? 1 : 0.5,
                            }}
                        >
                            <span>Next: Explain Why</span>
                            <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 3: DEBRIEF */}
            {step === 'DEBRIEF' && (
                <div style={styles.stepContent}>
                    <div style={styles.sectionHeader}>
                        <MessageCircle size={16} color="#34d399" />
                        <span style={styles.sectionTitle}>Why did you change this?</span>
                    </div>

                    <p style={styles.debriefHint}>
                        This is the most valuable training signal. Help your twin understand
                        your reasoning — the <em>why</em>, not just the <em>what</em>.
                    </p>

                    <textarea
                        value={reasoning}
                        onChange={e => setReasoning(e.target.value)}
                        style={styles.textarea}
                        rows={5}
                        placeholder="e.g. &quot;Sarah is a long-time client, we always use casual tone with her. I also added specific dates so she knows exactly what to expect.&quot;"
                        autoFocus
                    />

                    <div style={styles.actionRow}>
                        <button
                            onClick={() => setStep('CORRECT')}
                            style={styles.backBtn}
                        >
                            <ArrowLeft size={14} />
                            <span>Back</span>
                        </button>
                        <button
                            onClick={handleSubmitDebrief}
                            disabled={submitting}
                            style={{
                                ...styles.submitBtn,
                                opacity: submitting ? 0.7 : 1,
                            }}
                        >
                            <Send size={14} />
                            <span>{submitting ? 'Submitting...' : 'Submit & Complete'}</span>
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 4: COMPLETE */}
            {step === 'COMPLETE' && result && (
                <div style={styles.completeCard}>
                    <div style={styles.completeIcon}>
                        {result.action === 'approve' ? '✅' : '🎓'}
                    </div>
                    <h4 style={styles.completeTitle}>
                        {result.action === 'approve'
                            ? 'Approved! Your replica got it right.'
                            : 'Taught! Your replica learned from this.'}
                    </h4>
                    <p style={styles.completeDesc}>
                        {result.action === 'approve'
                            ? 'Approval recorded as a positive signal. Acceptance rate and style match will increase.'
                            : `Correction, ${result.tags?.length || 0} tags, and your reasoning have been recorded. All 3 learning channels updated.`}
                    </p>

                    <div style={styles.channelUpdates}>
                        <div style={styles.channelBadge}>
                            <Eye size={12} color="var(--accent)" /> Shadow +1
                        </div>
                        {result.action === 'approve' && (
                            <div style={styles.channelBadge}>
                                <ThumbsUp size={12} color="#34d399" /> Approval +1
                            </div>
                        )}
                        {result.action === 'correct' && (
                            <>
                                <div style={styles.channelBadge}>
                                    <PenLine size={12} color="#f472b6" /> Correct +1
                                </div>
                                <div style={styles.channelBadge}>
                                    <MessageCircle size={12} color="#34d399" /> Debrief +1
                                </div>
                            </>
                        )}
                    </div>

                    <button
                        onClick={onClose}
                        style={styles.doneBtn}
                    >
                        Done
                    </button>
                </div>
            )}
        </div>
    );
}

// ============================================
// STYLES
// ============================================

const styles: Record<string, React.CSSProperties> = {
    container: {
        background: 'linear-gradient(135deg, rgba(15,15,25,0.98), rgba(20,15,35,0.98))',
        borderRadius: 20,
        border: '1px solid rgba(167,139,250,0.15)',
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        maxWidth: 700,
        width: '100%',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    title: {
        fontSize: 18,
        fontWeight: 700,
        color: '#fff',
        margin: 0,
    },
    closeBtn: {
        background: 'transparent',
        border: 'none',
        color: 'rgba(255,255,255,0.4)',
        cursor: 'pointer',
        padding: 4,
    },
    // Step indicator
    stepIndicator: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
    },
    stepRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    stepDot: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        transition: 'all 0.3s ease',
    },
    stepLabel: {
        fontSize: 12,
        letterSpacing: 0.3,
    },
    stepConnector: {
        width: 40,
        height: 2,
        borderRadius: 1,
        margin: '0 8px',
        transition: 'background-color 0.3s ease',
    },
    // Step content
    stepContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    contextCard: {
        background: 'rgba(96,165,250,0.05)',
        border: '1px solid rgba(96,165,250,0.15)',
        borderRadius: 12,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    contextBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        color: 'var(--accent)',
        textTransform: 'uppercase' as const,
        letterSpacing: 0.5,
        fontWeight: 600,
    },
    contextText: {
        fontSize: 14,
        color: 'rgba(255,255,255,0.8)',
        lineHeight: 1.5,
        margin: 0,
    },
    replicaResponse: {
        background: 'rgba(167,139,250,0.05)',
        border: '1px solid rgba(167,139,250,0.15)',
        borderRadius: 12,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    responseHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    responseTitle: {
        fontSize: 12,
        fontWeight: 600,
        color: '#a78bfa',
    },
    responseText: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.85)',
        lineHeight: 1.6,
        margin: 0,
        whiteSpace: 'pre-wrap',
        fontFamily: 'inherit',
    },
    // Action buttons
    actionRow: {
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end',
    },
    approveBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 20px',
        borderRadius: 12,
        border: '1px solid rgba(52,211,153,0.3)',
        background: 'rgba(52,211,153,0.1)',
        color: '#34d399',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    correctBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 20px',
        borderRadius: 12,
        border: 'none',
        background: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: '0 4px 16px rgba(167,139,250,0.3)',
    },
    backBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '10px 16px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'transparent',
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        cursor: 'pointer',
    },
    nextBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '10px 20px',
        borderRadius: 10,
        border: 'none',
        background: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
    },
    submitBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '12px 24px',
        borderRadius: 12,
        border: 'none',
        background: 'linear-gradient(135deg, #34d399, #059669)',
        color: '#fff',
        fontSize: 14,
        fontWeight: 700,
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(52,211,153,0.3)',
    },
    // Textarea
    textarea: {
        width: '100%',
        padding: 14,
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(255,255,255,0.03)',
        color: '#fff',
        fontSize: 13,
        lineHeight: 1.6,
        resize: 'vertical' as const,
        fontFamily: 'inherit',
        outline: 'none',
    },
    // Section headers
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    sectionTitle: {
        fontSize: 15,
        fontWeight: 600,
        color: '#fff',
    },
    // Tags
    tagsGrid: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 8,
    },
    tagBtn: {
        padding: '6px 12px',
        borderRadius: 20,
        border: '1px solid',
        fontSize: 12,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        background: 'transparent',
    },
    // Debrief
    debriefHint: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.5)',
        lineHeight: 1.5,
        margin: 0,
    },
    // Complete
    completeCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        padding: '24px 0',
    },
    completeIcon: {
        fontSize: 48,
    },
    completeTitle: {
        fontSize: 18,
        fontWeight: 700,
        color: '#fff',
        margin: 0,
        textAlign: 'center' as const,
    },
    completeDesc: {
        fontSize: 13,
        color: 'rgba(255,255,255,0.5)',
        textAlign: 'center' as const,
        lineHeight: 1.5,
        margin: 0,
        maxWidth: 400,
    },
    channelUpdates: {
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap' as const,
        justifyContent: 'center',
    },
    channelBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 12px',
        borderRadius: 20,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        fontSize: 12,
        color: 'rgba(255,255,255,0.7)',
    },
    doneBtn: {
        padding: '10px 32px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'transparent',
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        marginTop: 8,
    },
};
